<template>
  <div class="pl-chats-list__area" ref="chartArea">
    <div v-if="chatList.length === 0 && !searchStr" class="pl-chat__messages pl-chat__messages--chat-list-empty">
      У вас пока нет чатов.
    </div>

    <div class="pl-chats-list__search">
      <v-text-field
        v-model="searchStr"
        :loading="loading"
        :dark="true"
        class="pl-chats-list__search-string"
        label="Поиск"
        clearable
        @keyup.enter="searchHandler()"
        @input="handlerEventInput(searchStr)"
        @click:clear="clearSearchString()"
      />
      <!--      <v-btn icon @click="searchHandler()" class="pl-chats-list__search-action">-->
      <!--        <v-icon color="white">search</v-icon>-->
      <!--      </v-btn>-->
    </div>

    <h5 v-if="chatList.length !== 0" class="pl-chats-list__title">Чаты:</h5>

    <ul class="pl-chats-list">
      <li
        v-for="(chat, index) in formattedChatList"
        :key="chat.id"
        :class="{
          'pl-chats-list__item--active': activeChatId === chat.id && idSelectedUser === null,
          'pl-chats-list__item--pinned': chat.isPinned,
        }"
        :ref="formattedChatList.length - 1 === index ? 'lastChatItem' : ''"
        class="pl-chats-list__item"
        @click="$emit('chat-selected', chat.id)"
      >
        <div class="pl-chats-list__item-avatar">
          <pl-chat-avatar :entity="chat" :icon="getAvatarIconType(chat.type)" />
        </div>

        <div class="pl-chats-list__item-body">
          <span v-if="showNotification(chat)" class="pl-chats-list__item-badge" />
          <div class="pl-chats-list__item-title">
            {{ getChatName(chat) }}
          </div>
          <div class="pl-chats-list__item-subtitle">
            {{ chatDescription(chat) }}
          </div>
          <v-icon class="pl-chats-list__pin-btn" color="#726c6c" size="20" @click.stop="pinHandler(chat)">
            push_pin
          </v-icon>
        </div>
      </li>
    </ul>

    <v-divider dark />

    <div v-if="searchStr">
      <h5 class="pl-chats-list__title">Сообщения:</h5>

      <ul class="pl-chats-list">
        <li
          v-for="message in messagesBySearch"
          :key="message.id"
          class="pl-chats-list__item"
          @click="clickHandlerOnSearchMessage(message)"
        >
          <div class="pl-chats-list__item-avatar">
            <pl-chat-avatar :entity="message" :icon="getAvatarIconType(message.type)" />
          </div>

          <div v-if="message.user" class="pl-chats-list__item-body">
            <span v-if="showNotification(message)" class="pl-chats-list__item-badge" />
            <div class="pl-chats-list__item-title">
              {{ message.user.name }}
            </div>
            <div class="pl-chats-list__item-subtitle">
              {{ message.user.name }}
              :
              {{ message.message }}
            </div>
          </div>
        </li>
      </ul>
      <v-divider dark />
    </div>

    <h5 class="pl-chats-list__title">Пользователи:</h5>

    <ul class="pl-chats-list pl-chats-list__users">
      <li
        v-for="user in users"
        :key="user.id"
        :class="{ 'pl-chats-list__item--active': idSelectedUser === user.id }"
        class="pl-chats-list__item"
        @click="$emit('user-selected', user.id)"
      >
        <div class="pl-chats-list__item-avatar">
          <pl-chat-avatar :entity="userInChat(user) ? user : {}" icon="person" />
        </div>

        <div class="pl-chats-list__item-body">
          <div class="pl-chats-list__item-title">
            {{ getCorporateUserName(user) }}
          </div>
        </div>
      </li>
    </ul>

    <div v-show="searchLoading" class="pl-chats-list__loader">
      <preloader :size="50" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import PlChatAvatar from './PlChatAvatar'
import debounce from './debounce'
import Preloader from '@/components/Preloader/Preloader'
import { pinAndUnpinChat, searchMessages } from '@/api/plChat'
import moment from 'moment'

function isFirstChatBeforeSecondChat(firstChat, secondChat) {
  return moment(firstChat.lastMessage?.updatedAt ?? firstChat.updatedAt).isBefore(
    secondChat.lastMessage?.updatedAt ?? secondChat.updatedAt,
  )
}

export default {
  name: 'PlChatList',
  components: {
    PlChatAvatar,
    Preloader,
  },
  data() {
    return {
      searchStr: '',
      loading: false,
      searchLoading: false,
      messagesBySearch: [],
      getAllSearchedMessages: false,
      currentPage: 1,
      currentChatPage: 1,
    }
  },
  props: {
    activeChatId: {
      type: Number || null,
      default: null,
    },
    idSelectedUser: {
      type: Number || null,
      default: null,
    },
    users: {
      type: Array,
      default: () => {
        return []
      },
    },
    chatList: {
      type: Array,
      default: () => {
        return []
      },
    },
    getChatName: {
      type: Function,
      required: true,
    },
  },
  mounted() {
    const area = document.querySelector('.pl-chats-list__area')

    area.addEventListener('scroll', this.listenScroll)
  },
  computed: {
    ...mapGetters(['lastChatPage']),
    formattedChatList() {
      const existingChats = this.chatList
        .filter(chat => chat.id !== -1 && this.showChat(this.searchStr, chat.name))
        .sort((firstChat, secondChat) => {
          return isFirstChatBeforeSecondChat(firstChat, secondChat) ? 1 : -1
        })
      const unpinnedChats = existingChats.filter(chat => !chat.isPinned)
      const pinnedChats = existingChats.filter(chat => chat.isPinned)

      return [...pinnedChats, ...unpinnedChats]
    },
  },
  methods: {
    ...mapMutations(['changeDataInTargetChatItem', 'clearUsers']),
    ...mapActions(['getUsers', 'searchUsers']),
    async createIntersection() {
      await this.$nextTick()
      const options = {
        root: this.$refs.chartArea,
        rootMargin: '0px',
        threshold: 1.0,
      }
      const callback = ([targetEntry], observer) => {
        if (targetEntry.isIntersecting && this.lastChatPage !== this.currentChatPage) {
          this.currentChatPage += 1
          this.$emit('loadMoreChats', this.currentChatPage)
        } else if (this.lastChatPage === this.currentChatPage) {
          observer.unobserve(target)
        }
      }
      const observer = new IntersectionObserver(callback, options)

      const [target] = this.$refs.lastChatItem

      if (target) {
        observer.observe(target)
      }
    },
    async pinHandler(chatData) {
      try {
        await pinAndUnpinChat(chatData.id)

        this.changeDataInTargetChatItem({
          ...chatData,
          isPinned: !chatData.isPinned,
        })
      } catch {
        this.$notify({
          group: 'hasBottomNavigation',
          type: 'error',
          text: 'Ошибка закрепления чата',
        })
      }
    },
    chatDescription(chat) {
      if (chat.hasOwnProperty('deletedAt') && !!chat.deletedAt) {
        return `Чат удален ${this.$formatDate(chat.deletedAt, 'DD.MM.YYYY')}`
      }
      if (chat.hasOwnProperty('closedAt') && !!chat.closedAt) {
        return `Чат заблокирован ${this.$formatDate(chat.closedAt, 'DD.MM.YYYY')}`
      }
      if (chat.hasOwnProperty('messages') && chat.messages.length) {
        return this.$formatDate(chat.messages[0].updatedAt, 'DD.MM.YYYY HH:mm')
      }

      return this.$formatDate(chat.lastMessage?.updatedAt ?? chat.updatedAt, 'DD.MM.YYYY HH:mm')
    },
    getAvatarIconType(chatType) {
      return chatType === 'public' ? 'people' : 'person'
    },
    userInChat(user) {
      return this.chatList.some(chat => {
        return chat.users.some(chatUser => chatUser.id === user.id)
      })
    },
    showChat(filterChatList, name) {
      if (filterChatList === null) {
        filterChatList = ''
      }

      const lowerName = name.toLowerCase()
      const lowerChatList = filterChatList.toLowerCase()

      return lowerName.includes(lowerChatList)
    },
    checkFilter(str, filterStr) {
      const lowerStr = str.toLowerCase()
      const lowerFilterStr = filterStr.toLowerCase()

      return lowerStr.includes(lowerFilterStr)
    },
    sortUsers(arrayUsers) {
      return arrayUsers.sort((a, b) => {
        if (a.name > b.name) return 1
        if (a.name === b.name) return 0
        if (a.name < b.name) return -1
      })
    },
    getCorporateUserName(user) {
      const endPhoneNumber = user.personalData.phone.slice(-4)

      return `${user.name} ${endPhoneNumber}`
    },
    showNotification(chat) {
      const chatUpdatedBySocket = chat.hasOwnProperty('newMessageNotify') ? chat.newMessageNotify : false
      return chatUpdatedBySocket || chat.notReadYet
    },
    normalizeSearchString(value) {
      if (value === null) {
        this.searchStr = ''
      }
    },
    clearSearchString() {
      this.searchStr = ''
      this.getAllSearchedMessages = false
      this.messagesBySearch = []
      this.searchHandler()
    },
    handlerEventInput(value) {
      if (value) {
        this.handlerEventInputDebounce()
      } else {
        this.searchHandler()
        this.clearSearchString()
      }
    },
    async searchHandler() {
      this.currentPage = 1
      this.loading = true

      if (this.searchStr) {
        const promises = [this.searchUsers({ searchStr: this.searchStr }), this.searchMessages()]
        await Promise.all(promises)
      } else {
        this.clearUsers()
        await this.getUsers({
          reloading: false,
          limit: null,
          offset: null,
        })
      }
      this.loading = false
    },
    async searchMessages(scrollSearchIsDone = false) {
      if (scrollSearchIsDone) {
        return
      }

      const {
        data: { data, per_page: messagesOnPerPage },
      } = await searchMessages({
        search: this.searchStr,
        page: this.currentPage,
      })

      if (data.length < messagesOnPerPage) {
        this.getAllSearchedMessages = true
      }

      if (this.currentPage === 1) {
        this.messagesBySearch = data
      } else {
        this.messagesBySearch = [...this.messagesBySearch, ...Object.values(data)]
      }

      this.currentPage += 1
    },
    clickHandlerOnSearchMessage({ chat_id: chatId, id: messageId }) {
      this.$emit('message-selected', {
        messageId: messageId,
        refKey: `message_${messageId}`,
        chatId,
      })
    },
    listenScroll() {
      const area = document.querySelector('.pl-chats-list__area')
      const areaBottom = area.getBoundingClientRect().bottom
      const lastUser = document.querySelector('.pl-chats-list__users')
      const lastUserTop = lastUser.getBoundingClientRect().bottom - 96

      if (areaBottom > lastUserTop && this.searchStr === '') {
        this.searchLoading = true
        this.handlerLoadUsers()
      } else if (areaBottom > lastUserTop) {
        this.handlerSearchMessages()
      }
    },
    async loadUsers() {
      const quantityUsers = this.$store.state.plChat.quantityUsers
      const offset = this.$store.state.plChat.offset
      const limit = quantityUsers - (offset % quantityUsers)

      await this.getUsers({
        reloading: true,
        limit: limit,
        offset: offset,
      })

      this.searchLoading = false
    },
    handlerEventInputDebounce: debounce(
      function () {
        this.searchHandler()
      },
      600,
      false,
    ),
    handlerSearchMessages: debounce(
      function () {
        this.searchMessages(this.getAllSearchedMessages)
      },
      200,
      false,
    ),
    handlerLoadUsers: debounce(
      function () {
        this.loadUsers()
      },
      1000,
      false,
    ),
  },
  watch: {
    chatList(newChatList) {
      if (newChatList?.length) {
        this.createIntersection()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.pl-chats-list {
  list-style-type: none;
  padding: 0.5rem;

  &__area {
    max-height: calc(85vh - 48px - 1rem);
    overflow-y: scroll;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    padding-left: 0.5rem;
    border-radius: 2px;
    border: 1px solid rgba(0, 0, 0, 0);

    &:hover {
      border-color: #36afbb;

      .pl-chats-list__pin-btn {
        display: block;
      }
    }

    &--active {
      background-color: rgba(0, 0, 0, 0.2);
    }

    &--pinned {
      .pl-chats-list__pin-btn {
        display: block;
        transform: rotate(-45deg);
      }
    }
  }

  &__title {
    padding: 0.5rem 2rem 0.2rem 2rem;
  }

  &__filter {
    padding: 0.2rem 1rem;
  }

  &__search {
    display: flex;
    padding: 0.5rem 1rem 0;
    align-items: flex-end;
  }

  &__search-action {
  }

  &__item-avatar {
  }

  &__item-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    max-height: 4rem;
    overflow: hidden;
    line-height: 1.6;
    position: relative;
  }

  &__item-badge {
    position: absolute;
    width: 11px;
    height: 11px;
    background-color: #36afbb;
    border-radius: 50%;
    top: 0.5rem;
    right: 0.5rem;
  }

  &__item-title {
    text-overflow: clip;
    overflow: hidden;
    cursor: pointer;
  }

  &__item-subtitle {
    text-overflow: ellipsis;
    font-weight: lighter;
    font-size: 0.8rem;
    cursor: pointer;
  }

  &__loader {
    padding: 0.5rem 1rem 1rem;
  }

  &__pin-btn {
    position: absolute;
    bottom: 5px;
    right: 5px;
    display: none;
    transition: transform 0s;
  }
}
</style>
